import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fetchProducts } from '../../store/slices/productSlice';
import api from '../../services/api';
import {
  Container, Grid, Card, CardMedia, CardContent, Typography, Box,
  TextField, Select, MenuItem, FormControl, InputLabel, Pagination,
  Chip, CircularProgress, InputAdornment, Button, IconButton, Dialog,
  DialogContent, DialogTitle, Divider
} from '@mui/material';
import { Search, ImageSearch, Close, Add, Link as LinkIcon } from '@mui/icons-material';

const PAGE_SIZE = 20;

const sortProducts = (items, sort) => {
  const sorted = [...items];

  if (sort === 'price_asc') {
    sorted.sort((left, right) => (left.pricing?.basePrice || 0) - (right.pricing?.basePrice || 0));
  } else if (sort === 'price_desc') {
    sorted.sort((left, right) => (right.pricing?.basePrice || 0) - (left.pricing?.basePrice || 0));
  } else if (sort === 'popular') {
    sorted.sort((left, right) => (right.stats?.views || 0) - (left.stats?.views || 0));
  } else {
    sorted.sort((left, right) => new Date(right.createdAt || 0) - new Date(left.createdAt || 0));
  }

  return sorted;
};

const flattenProductSearchValues = (value) => {
  if (value === null || value === undefined) return [];

  if (Array.isArray(value)) {
    return value.flatMap((entry) => flattenProductSearchValues(entry));
  }

  if (typeof value === 'object') {
    return Object.values(value).flatMap((entry) => flattenProductSearchValues(entry));
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return [String(value)];
  }

  return [];
};

const getProductSearchText = (product) => {
  const searchableValues = [
    product.name,
    product.nameAr,
    product.osesgCode,
    product.supplierProductCode,
    product.shortDescription,
    product.description,
    product.descriptionAr,
    product.subCategory,
    product.approvalNotes,
    product.category?.name,
    product.category?.slug,
    product.applications,
    product.advantages,
    product.specifications,
    product.documents,
    product.keywords,
    product.tags,
    product.pricing,
    product.shipping,
    product.customManufacturingDetails
  ];

  return flattenProductSearchValues(searchableValues).join(' ').toLowerCase();
};

const ProductsPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { products, loading } = useSelector(state => state.products);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [sort, setSort] = useState('newest');
  const [page, setPage] = useState(1);
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get('category') || 'all');
  const [priceTypeFilter, setPriceTypeFilter] = useState('all');
  const [groupBy, setGroupBy] = useState('none');
  const [pastedImageFile, setPastedImageFile] = useState(null);
  const [pastedImagePreview, setPastedImagePreview] = useState('');
  const [imageSearchLoading, setImageSearchLoading] = useState(false);
  const [imageSearchError, setImageSearchError] = useState('');
  const [imageSearchResults, setImageSearchResults] = useState([]);
  const [lensOpen, setLensOpen] = useState(false);
  const [imageLink, setImageLink] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    dispatch(fetchProducts({ page: 1, limit: 500 }));
  }, [dispatch]);

  useEffect(() => {
    setPage(1);
  }, [search, sort, categoryFilter, priceTypeFilter, groupBy]);

  useEffect(() => () => {
    if (pastedImagePreview) {
      URL.revokeObjectURL(pastedImagePreview);
    }
  }, [pastedImagePreview]);

  const clearPastedImage = () => {
    if (pastedImagePreview) {
      URL.revokeObjectURL(pastedImagePreview);
    }

    setPastedImageFile(null);
    setPastedImagePreview('');
    setImageSearchResults([]);
    setImageSearchError('');
    setImageLink('');
  };

  const setSelectedImageFile = (file) => {
    if (!file) {
      return;
    }

    if (pastedImagePreview) {
      URL.revokeObjectURL(pastedImagePreview);
    }

    setPastedImageFile(file);
    setPastedImagePreview(URL.createObjectURL(file));
    setImageLink('');
    setImageSearchError('');
    setPage(1);
  };

  const handleLensPaste = (event) => {
    const items = Array.from(event.clipboardData?.items || []);
    const imageItem = items.find((item) => item.type.startsWith('image/'));

    if (!imageItem) {
      return;
    }

    event.preventDefault();
    const file = imageItem.getAsFile();
    setSelectedImageFile(file);
  };

  const handleDroppedImage = (event) => {
    event.preventDefault();
    const [file] = Array.from(event.dataTransfer?.files || []);
    if (file && file.type.startsWith('image/')) {
      setSelectedImageFile(file);
    }
  };

  const handleFileInputChange = (event) => {
    const [file] = Array.from(event.target.files || []);
    if (file && file.type.startsWith('image/')) {
      setSelectedImageFile(file);
    }
  };

  const runImageSearch = async () => {
    if (!pastedImageFile && !imageLink.trim()) {
      setImageSearchError('Add an image or paste an image link');
      return;
    }

    try {
      setImageSearchLoading(true);
      setImageSearchError('');

      const visualSearchFallbackBase = process.env.REACT_APP_VISUAL_SEARCH_URL || 'http://localhost:5001/api/v1';

      const executeVisualSearch = async (endpoint) => {
        if (pastedImageFile) {
          const formData = new FormData();
          formData.append('image', pastedImageFile, pastedImageFile.name || 'lens-image.png');
          formData.append('limit', '24');
          // Use a bare axios call via the api instance but bypass the JSON
          // default Content-Type by using `transformRequest` to return the
          // FormData untouched. Browser will then set the correct
          // multipart/form-data header with boundary.
          const token = localStorage.getItem('token');
          const baseURL = api.defaults.baseURL || '/api/v1';
          const url = endpoint.startsWith('http') ? endpoint : `${baseURL}${endpoint}`;
          const xhr = await fetch(url, {
            method: 'POST',
            body: formData,
            credentials: 'include',
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          const json = await xhr.json().catch(() => ({}));
          if (!xhr.ok) {
            const err = new Error(json?.error || `HTTP ${xhr.status}`);
            err.response = { status: xhr.status, data: json };
            throw err;
          }
          return { data: json };
        }

        return api.post(endpoint, {
          imageUrl: imageLink.trim(),
          limit: 24
        });
      };

      let response;
      try {
        response = await executeVisualSearch('/products/visual-search');
      } catch (primaryError) {
        if (primaryError.response?.status === 404) {
          response = await executeVisualSearch(`${visualSearchFallbackBase}/products/visual-search`);
        } else {
          throw primaryError;
        }
      }

      const data = response.data;

      setImageSearchResults(data.data || []);
      setLensOpen(false);
      setPage(1);
    } catch (error) {
      setImageSearchResults([]);
      const status = error.response?.status;
      const serverError = error.response?.data?.error;

      if (status === 404) {
        setImageSearchError('Backend image-search route is not available. Please restart backend and try again.');
      } else if (status === 413) {
        setImageSearchError('Image is too large. Please use a smaller file and try again.');
      } else if (status === 400) {
        setImageSearchError(serverError || 'Invalid image input. Upload an image or paste a valid image link.');
      } else if (status === 500) {
        setImageSearchError(serverError || 'Server error during image search. Please try again.');
      } else if (!error.response) {
        setImageSearchError('Cannot reach backend service. Check if backend is running on port 5000.');
      } else {
        setImageSearchError(serverError || `Image search failed (HTTP ${status || 'unknown'}).`);
      }
    } finally {
      setImageSearchLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();

    const nextParams = {};
    if (search.trim()) nextParams.search = search.trim();
    if (categoryFilter !== 'all') nextParams.category = categoryFilter;
    setSearchParams(nextParams);
    setPage(1);
  };

  const categoryMatches = (product, value) => {
    if (value === 'all') return true;

    return [product.category?._id, product.category?.name, product.category?.slug]
      .filter(Boolean)
      .some((entry) => entry === value);
  };

  const categoryOptions = ['all', ...new Set(products.map((product) => product.category?.name).filter(Boolean))];
  const priceTypeOptions = ['all', ...new Set(products.map((product) => product.pricing?.priceType).filter(Boolean))];
  const usingImageSearch = Boolean(pastedImageFile || imageLink.trim() || imageSearchResults.length);
  const sourceProducts = usingImageSearch ? imageSearchResults : products;

  const filteredSourceProducts = sourceProducts.filter((product) => {
      const searchValue = search.trim().toLowerCase();
      const matchesSearch = !searchValue || getProductSearchText(product).includes(searchValue);

      const matchesCategory = categoryMatches(product, categoryFilter);
      const matchesPriceType = priceTypeFilter === 'all' || product.pricing?.priceType === priceTypeFilter;

      return matchesSearch && matchesCategory && matchesPriceType;
    });

  const filteredProducts = usingImageSearch
    ? [...filteredSourceProducts].sort((left, right) => (right.visualSimilarity || 0) - (left.visualSimilarity || 0))
    : sortProducts(filteredSourceProducts, sort);

  const pageCount = Math.ceil(filteredProducts.length / PAGE_SIZE) || 1;
  const paginatedProducts = filteredProducts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const getGroupValue = (product) => {
    if (groupBy === 'category') return product.category?.name || 'Uncategorized';
    if (groupBy === 'priceType') return product.pricing?.priceType || 'Unspecified Price Type';
    return 'All Products';
  };

  const groupedProducts = groupBy === 'none'
    ? [{ label: null, items: paginatedProducts }]
    : Object.entries(
        paginatedProducts.reduce((groups, product) => {
          const key = getGroupValue(product);
          if (!groups[key]) groups[key] = [];
          groups[key].push(product);
          return groups;
        }, {})
      ).map(([label, items]) => ({ label, items }));

  const resetFilters = () => {
    setSearch('');
    setSort('newest');
    setCategoryFilter('all');
    setPriceTypeFilter('all');
    setGroupBy('none');
    setSearchParams({});
    setPage(1);
    clearPastedImage();
  };

  const openLensDialog = () => {
    setImageSearchError('');
    setLensOpen(true);
  };

  const openFilePicker = () => {
    setLensOpen(true);
    window.requestAnimationFrame(() => {
      fileInputRef.current?.click();
    });
  };

  return (
    <>
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Search & Filters */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Box component="form" onSubmit={handleSearch} sx={{ width: '100%', maxWidth: 920 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <IconButton
                onClick={openFilePicker}
                aria-label="Upload files or images"
                sx={{
                  width: 52,
                  height: 52,
                  bgcolor: '#fff',
                  border: '1px solid #e3e7ee',
                  boxShadow: '0 2px 8px rgba(60,64,67,.15)',
                  '&:hover': { bgcolor: '#f8f9fa' }
                }}
              >
                <Add />
              </IconButton>
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: 58,
                  pl: 2,
                  pr: 1,
                  borderRadius: '999px',
                  bgcolor: '#fff',
                  border: '1px solid #e3e7ee',
                  boxShadow: '0 2px 10px rgba(60,64,67,.15)'
                }}
              >
                <Search sx={{ color: '#5f6368', mr: 1.5 }} />
                <TextField
                  fullWidth
                  variant="standard"
                  placeholder={usingImageSearch ? 'Search products in your database from the selected image' : 'Search products'}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{ disableUnderline: true }}
                />
                <Divider orientation="vertical" flexItem sx={{ mx: 1, my: 1.5 }} />
                <IconButton onClick={openLensDialog} aria-label="Search by image">
                  <ImageSearch color={usingImageSearch ? 'primary' : 'inherit'} />
                </IconButton>
              </Box>
              <Button type="submit" variant="contained" disabled={imageSearchLoading} sx={{ minWidth: 120, borderRadius: 3, textTransform: 'none' }}>
                Search
              </Button>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2, justifyContent: 'center' }}>
          <Chip icon={<ImageSearch />} label="Search by image / photo" variant="outlined" />
          <Typography variant="body2" color="text.secondary">
            Upload or paste a photo — we match it against product images on this website.
          </Typography>
        </Box>

        {pastedImagePreview && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Box
              component="img"
              src={pastedImagePreview}
              alt="Pasted product area"
              sx={{ width: 88, height: 88, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={700}>Image search active</Typography>
              <Typography variant="body2" color="text.secondary">
                Showing results from your own catalog based on the selected image.
              </Typography>
              {imageSearchError && (
                <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                  {imageSearchError}
                </Typography>
              )}
            </Box>
            <IconButton onClick={clearPastedImage} aria-label="Clear pasted image">
              <Close />
            </IconButton>
          </Box>
        )}

        {imageLink && !pastedImagePreview && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={700}>Image link search active</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                {imageLink}
              </Typography>
            </Box>
            <IconButton onClick={clearPastedImage} aria-label="Clear image link">
              <Close />
            </IconButton>
          </Box>
        )}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select value={categoryFilter} label="Category" onChange={(e) => setCategoryFilter(e.target.value)}>
                {categoryOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option === 'all' ? 'All Categories' : option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Price Type</InputLabel>
              <Select value={priceTypeFilter} label="Price Type" onChange={(e) => setPriceTypeFilter(e.target.value)}>
                {priceTypeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option === 'all' ? 'All Price Types' : option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Group By</InputLabel>
              <Select value={groupBy} label="Group By" onChange={(e) => setGroupBy(e.target.value)}>
                <MenuItem value="none">No Grouping</MenuItem>
                <MenuItem value="category">Category</MenuItem>
                <MenuItem value="priceType">Price Type</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="outlined" onClick={resetFilters} sx={{ height: '100%' }}>
              Reset Filters
            </Button>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
              {filteredProducts.length} products found
            </Typography>
            <Chip label={`${filteredProducts.length} products`} color="primary" variant="outlined" />
            {groupBy !== 'none' && <Chip label={`${groupedProducts.length} groups`} variant="outlined" />}
          </Box>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Sort by</InputLabel>
            <Select value={sort} onChange={(e) => setSort(e.target.value)} label="Sort by">
              <MenuItem value="newest">Newest</MenuItem>
              <MenuItem value="price_asc">Price: Low to High</MenuItem>
              <MenuItem value="price_desc">Price: High to Low</MenuItem>
              <MenuItem value="popular">Most Popular</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Products Grid */}
      {loading || imageSearchLoading ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : (
        <>
          {groupedProducts.map((group) => (
            <Box key={group.label || 'all-products'} sx={{ mb: 4 }}>
              {group.label && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="h6" fontWeight={700}>{group.label}</Typography>
                  <Chip label={`${group.items.length} items`} size="small" variant="outlined" />
                </Box>
              )}
              <Grid container spacing={3}>
                {group.items.map((product) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={product._id}>
                    <Card
                      component={Link} to={`/products/${product._id}`}
                      sx={{
                        textDecoration: 'none', height: '100%', display: 'flex', flexDirection: 'column',
                        '&:hover': { transform: 'translateY(-4px)', transition: '0.3s', boxShadow: 6 }
                      }}
                    >
                      <CardMedia
                        component="div"
                        sx={{ height: 200, bgcolor: '#e8eaf6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        {product.images?.[0] ? (
                          <Box
                            role="img"
                            aria-label={product.name}
                            sx={{
                              width: '100%',
                              height: '100%',
                              backgroundImage: `url(${product.images[0].url})`,
                              backgroundRepeat: 'no-repeat',
                              backgroundPosition: 'center',
                              backgroundSize: 'contain'
                            }}
                          />
                        ) : (
                          <Typography variant="h4" color="text.disabled">📦</Typography>
                        )}
                      </CardMedia>
                      <CardContent sx={{ flex: 1 }}>
                        <Typography variant="caption" color="primary" fontWeight={600}>
                          {product.osesgCode}
                        </Typography>
                        {typeof product.visualSimilarity === 'number' && (
                          <Chip
                            label={`${Math.round(product.visualSimilarity * 100)}% match`}
                            size="small"
                            color="success"
                            sx={{ ml: 1, mb: 1 }}
                          />
                        )}
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom
                          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                          {product.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1,
                          overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                          {product.shortDescription || product.description?.substring(0, 100)}
                        </Typography>
                        <Box sx={{ mt: 'auto' }}>
                          {product.pricing?.priceType === 'negotiable' ? (
                            <Chip label="Negotiable" size="small" color="secondary" />
                          ) : (
                            <Typography variant="h6" color="primary" fontWeight={700}>
                              ${product.pricing?.basePrice?.toLocaleString()}
                              {product.pricing?.priceType === 'range' &&
                                ` - $${product.pricing?.maxPrice?.toLocaleString()}`}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" display="block">
                            MOQ: {product.pricing?.moq || 1} | Lead: {product.shipping?.leadTime} days
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}

          {filteredProducts.length === 0 && (
            <Box textAlign="center" py={8}>
              <Typography variant="h6" color="text.secondary">{t('no_results')}</Typography>
            </Box>
          )}

          {pageCount > 1 && filteredProducts.length > 0 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination count={pageCount} page={page} onChange={(e, v) => setPage(v)} color="primary" />
            </Box>
          )}
        </>
      )}
    </Container>
    <Dialog open={lensOpen} onClose={() => setLensOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="h6" fontWeight={700}>Search by image</Typography>
          <IconButton onClick={() => setLensOpen(false)} aria-label="Close image search dialog">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <input ref={fileInputRef} hidden type="file" accept="image/*" onChange={handleFileInputChange} />
        <Box
          onPaste={handleLensPaste}
          onDragOver={(event) => event.preventDefault()}
          onDrop={handleDroppedImage}
          sx={{ border: '1px dashed', borderColor: '#d2d7e1', borderRadius: 4, p: 3, bgcolor: '#fff' }}
        >
          <Box sx={{ minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', backgroundColor: '#fafafa', borderRadius: 3, border: '1px dashed', borderColor: '#d7dbe7' }}>
            {pastedImagePreview ? (
              <Box sx={{ width: '100%' }}>
                <Box component="img" src={pastedImagePreview} alt="Selected for lens search" sx={{ maxHeight: 220, maxWidth: '100%', objectFit: 'contain', borderRadius: 2 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Image ready. Search will match only products from your own website catalog.
                </Typography>
              </Box>
            ) : (
              <Box>
                <ImageSearch sx={{ fontSize: 58, color: '#7b8794', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Drag an image here or upload a file
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  You can also paste a copied snip with Ctrl+V and search inside your database.
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button variant="text" onClick={() => fileInputRef.current?.click()} sx={{ textTransform: 'none' }}>
              upload a file
            </Button>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 3 }}>
            <Divider sx={{ flex: 1 }} />
            <Typography variant="body2" color="text.secondary">OR</Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', flexWrap: 'wrap' }}>
            <TextField
              fullWidth
              placeholder="Paste image link"
              value={imageLink}
              InputProps={{
                startAdornment: <InputAdornment position="start"><LinkIcon fontSize="small" /></InputAdornment>
              }}
              onChange={(event) => {
                setImageLink(event.target.value);
                if (event.target.value.trim()) {
                  if (pastedImagePreview) {
                    URL.revokeObjectURL(pastedImagePreview);
                  }
                  setPastedImagePreview('');
                  setPastedImageFile(null);
                }
                setImageSearchError('');
              }}
              sx={{ flex: 1, minWidth: 280 }}
            />
            <Button variant="contained" onClick={runImageSearch} disabled={imageSearchLoading} sx={{ minWidth: 140, borderRadius: 999, textTransform: 'none' }}>
              Search
            </Button>
          </Box>

          {imageSearchError && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {imageSearchError}
            </Typography>
          )}
        </Box>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default ProductsPage;
